import React, { useState } from 'react';
import { styled, useTheme } from '@mui/material/styles';
import { useLocation, Link } from 'react-router-dom'; // Importar Link para la navegación
import Box from '@mui/material/Box';
import MuiDrawer from '@mui/material/Drawer';
import MuiAppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import HomeIcon from '@mui/icons-material/Home'; // Icono de Home
import MovieIcon from '@mui/icons-material/Movie'; // Icono de Multimedia
import InfoIcon from '@mui/icons-material/Info'; // Icono de About
import ContactMailIcon from '@mui/icons-material/ContactMail'; // Icono de Contact
import mediaSVG from '../assets/images/SVG1.svg';
import useDeviceType from "../hooks/useDeviceType";

const drawerWidth = 240;

const openedMixin = (theme) => ({
    width: drawerWidth,
    backgroundColor: "#121212",
    borderWidth: "0px 1px 0px 0px", borderStyle: "solid", borderColor: "#2F2F2F",
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
    }),
    overflowX: 'hidden',
});

const closedMixin = (theme) => ({
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    overflowX: 'hidden',
    width: `calc(${theme.spacing(7)} + 1px)`,
    backgroundColor: "#121212",
    borderWidth: "0px 1px 0px 0px", borderStyle: "solid", borderColor: "#2F2F2F",
    [theme.breakpoints.up('sm')]: {
        width: `calc(${theme.spacing(8)} + 1px)`,
    },
});

const DrawerHeader = styled('div')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    padding: theme.spacing(0, 1),
    ...theme.mixins.toolbar,
}));

const AppBar = styled(MuiAppBar)(({ theme, open }) => ({
    zIndex: theme.zIndex.drawer + 1,
    transition: theme.transitions.create(['width', 'margin'], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    ...(open && {
        marginLeft: drawerWidth,
        width: `calc(100% - ${drawerWidth}px)`,
    }),
}));

const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(
    ({ theme, open }) => ({
        width: drawerWidth,
        flexShrink: 0,
        whiteSpace: 'nowrap',
        boxSizing: 'border-box',
        ...(open && {
            ...openedMixin(theme),
            '& .MuiDrawer-paper': openedMixin(theme),
        }),
        ...(!open && {
            ...closedMixin(theme),
            '& .MuiDrawer-paper': closedMixin(theme),
        }),
    })
);

export default function MiniDrawer({ children }) {
    const theme = useTheme();
    const [open, setOpen] = useState(false);
    const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);

    const toggleDrawer = () => {
        setOpen(!open);
    };

    const toggleMobileDrawer = () => {
        setMobileDrawerOpen(!mobileDrawerOpen);
    };

    // Datos de navegación
    const mainPages = [
        { name: 'Home', path: '/', icon: <HomeIcon /> },
        { name: 'Multimedia', path: '/multimedia', icon: <MovieIcon /> },
    ];

    const secondaryPages = [
        { name: 'About', path: '/about', icon: <InfoIcon /> },
        { name: 'Contact', path: '/contact', icon: <ContactMailIcon /> },
    ];

    const location = useLocation(); // Hook que te da la ruta actual
    const isSelected = (path) => location.pathname === path;

    const isPortrait = useDeviceType();

    const DrawerTelefono = ({ open, toggleDrawer }) => (
        <MuiDrawer
            anchor="top"
            open={open}
            onClose={toggleDrawer}
            sx={{
                width: "100%",
                height: "auto",
                backgroundColor: "#12121250",
                borderWidth: "0px 1px 0px 0px", borderStyle: "solid", borderColor: "#2F2F2F",
                '& .MuiDrawer-paper': {
                    backgroundColor: '#2F2F2F', // Cambia aquí el color de fondo
                },
            }}
        >
            {/* Aquí va el contenido del Drawer móvil */}
            <DrawerHeader sx={{ paddingRight: "0px", paddingLeft: "0px", height: "64.5px", width: "100%", borderWidth: "0px 0px 1px 0px", borderStyle: "solid", borderColor: "#414141", }}>
                <IconButton onClick={toggleDrawer} style={{ color: "white" }}>
                    {open ? <ChevronLeftIcon /> : <ChevronRightIcon />}
                </IconButton>
                <h4 style={{ color: "white", textAlign: "center", display: open ? 'block' : 'none' }}>Menú</h4>
            </DrawerHeader>
            <Divider />
            <List>
                <h6 style={{ color: "white", textAlign: "center", display: open ? 'block' : 'none' }}>Principal</h6>
                {mainPages.map((page) => (
                    <ListItem key={page.name} disablePadding sx={{ display: 'block' }}>
                        <ListItemButton
                            component={Link}
                            to={page.path}
                            onClick={toggleDrawer}
                            sx={{
                                minHeight: 48,
                                justifyContent: open ? 'initial' : 'center',
                                px: 2.5,
                                //backgroundColor: isSelected(page.path) ? theme.palette.success.main : 'transparent',
                                backgroundColor: isSelected(page.path) ? "#78787850" : 'transparent',
                                '&:hover': {
                                    //backgroundColor: theme.palette.success.light,
                                    backgroundColor: "#787878",
                                },
                            }}
                        >
                            <ListItemIcon
                                sx={{
                                    minWidth: 0,
                                    mr: open ? 3 : 'auto',
                                    justifyContent: 'center',
                                }}
                            >
                                <div style={{ color: isSelected(page.path) ? "#aaaaff" : '', }}>{page.icon}</div>
                            </ListItemIcon>
                            <ListItemText primary={page.name} sx={{ opacity: open ? 1 : 0, color: isSelected(page.path) ? "#ffffff" : '', }} />
                        </ListItemButton>
                    </ListItem>
                ))}
            </List>
            <Divider
                sx={{
                    borderColor: '#2F2F2F', // Cambiar el color del borde
                    borderWidth: '1px 0px 1px 0px', // Cambiar el grosor del borde
                    marginY: 0, // Espaciado vertical
                    marginX: 2, // Espaciado horizontal
                    height: "1px",

                }}
            />
            <List>
                <h6 style={{ color: "white", textAlign: "center", display: open ? 'block' : 'none' }}>Secundario</h6>
                {secondaryPages.map((page) => (
                    <ListItem key={page.name} disablePadding sx={{ display: 'block' }}>
                        <ListItemButton
                            component={Link}
                            to={page.path}
                            onClick={toggleDrawer}
                            sx={{
                                minHeight: 48,
                                justifyContent: open ? 'initial' : 'center',
                                px: 2.5,
                                //backgroundColor: isSelected(page.path) ? theme.palette.success.main : 'transparent',
                                backgroundColor: isSelected(page.path) ? "#78787850" : 'transparent',
                                '&:hover': {
                                    //backgroundColor: theme.palette.success.light,
                                    backgroundColor: "#787878",
                                },
                            }}
                        >
                            <ListItemIcon
                                sx={{
                                    minWidth: 0,
                                    mr: open ? 3 : 'auto',
                                    justifyContent: 'center',
                                }}
                            >
                                <div style={{ color: isSelected(page.path) ? "#aaaaff" : '', }}>{page.icon}</div>
                            </ListItemIcon>
                            <ListItemText primary={page.name} sx={{ opacity: open ? 1 : 0, color: isSelected(page.path) ? "#ffffff" : '', }} />
                        </ListItemButton>
                    </ListItem>
                ))}
            </List>
        </MuiDrawer>
    );

    return (
        <Box sx={{ display: 'flex', backgroundColor: "#121212" }}>


            {isPortrait ? (
                <div>
                    <AppBar position="fixed" open={open}
                        sx={{ height: "65px", backgroundColor: "#121212", borderWidth: "0px 0px 1px 0px", borderStyle: "solid", borderColor: "#414141" }}
                    > {/* Header AppBar Arriba */}
                        <Toolbar sx={{ paddingLeft: "0px!important", display: 'flex', justifyContent: 'space-between', paddingRight: "0px!important" }}>
                            <div style={{ height: '100%' }}>
                                <img src={mediaSVG} alt="SVG Gráfico" style={{ height: '100%', transform: isPortrait ? "scaleX(-1)" : 'scaleX(1)' }} />
                            </div>

                            <Typography variant="h6" noWrap>
                                <Link to="/" style={{ textDecoration: 'none', color: '#aaaaff' }}>
                                    <h1>xetc.top</h1>
                                </Link>
                            </Typography>

                            <IconButton
                                color="inherit"
                                aria-label="open drawer"
                                onClick={isPortrait ? toggleMobileDrawer/*Este abre el de telefono */ : toggleDrawer}
                                edge="start"
                                sx={{
                                    marginRight: 2,
                                    marginLeft: 4,
                                    ...(open && { display: 'none' }),
                                }}
                            >
                                <MenuIcon />
                            </IconButton>
                        </Toolbar>
                    </AppBar>
                </div>
            ) : (
                <div>
                    <AppBar position="fixed" open={open}
                        sx={{ height: "65px", backgroundColor: "#121212", borderWidth: "0px 0px 1px 0px", borderStyle: "solid", borderColor: "#414141" }}
                    > {/* Header AppBar Arriba */}
                        <Toolbar sx={{ display: 'flex', justifyContent: 'space-between', paddingRight: "0px!important" }}>
                            <IconButton
                                color="inherit"
                                aria-label="open drawer"
                                onClick={isPortrait ? toggleMobileDrawer/*Este abre el de telefono */ : toggleDrawer}
                                edge="start"
                                sx={{
                                    marginRight: 5,
                                    ...(open && { display: 'none' }),
                                }}
                            >
                                <MenuIcon />
                            </IconButton>
                            <Typography variant="h6" noWrap>
                                <Link to="/" style={{ textDecoration: 'none', color: '#aaaaff' }}>
                                    <h1>xetc.top</h1>
                                </Link>
                            </Typography>
                            <div style={{ marginLeft: 'auto', height: '100%' }}>
                                <img src={mediaSVG} alt="SVG Gráfico" style={{ height: '100%', transform: isPortrait ? "scaleX(-1)" : 'scaleX(1)' }} />
                            </div>
                        </Toolbar>
                    </AppBar>
                </div>
            )}


            <Drawer variant="permanent" open={isPortrait ? ""/*Este abre el de telefono*/ : open} anchor={isPortrait ? 'top' : 'left'} sx={{ display: isPortrait ? 'none' : 'block', }}>
                {isPortrait ? (
                    <div>
                        {/* Drawer emergente para móviles */}
                        <DrawerTelefono open={mobileDrawerOpen} toggleDrawer={toggleMobileDrawer} />
                    </div>
                ) : (
                    <div>
                        <DrawerHeader sx={{ height: "64.5px", width: "100%", borderWidth: "0px 0px 1px 0px", borderStyle: "solid", borderColor: "#414141", }}>
                            <IconButton onClick={toggleDrawer} style={{ color: "white" }}>
                                {open ? <ChevronLeftIcon /> : <ChevronRightIcon />}
                            </IconButton>
                            <h4 style={{ color: "white", textAlign: "center", display: open ? 'block' : 'none' }}>Menú</h4>
                        </DrawerHeader>
                        <Divider />
                        <List>
                            <h6 style={{ color: "white", textAlign: "center", display: open ? 'block' : 'none' }}>Principal</h6>
                            {mainPages.map((page) => (
                                <ListItem key={page.name} disablePadding sx={{ display: 'block' }}>
                                    <ListItemButton
                                        component={Link}
                                        to={page.path}
                                        sx={{
                                            minHeight: 48,
                                            justifyContent: open ? 'initial' : 'center',
                                            px: 2.5,
                                            //backgroundColor: isSelected(page.path) ? theme.palette.success.main : 'transparent',
                                            backgroundColor: isSelected(page.path) ? "#78787850" : 'transparent',
                                            '&:hover': {
                                                //backgroundColor: theme.palette.success.light,
                                                backgroundColor: "#787878",
                                            },
                                        }}
                                    >
                                        <ListItemIcon
                                            sx={{
                                                minWidth: 0,
                                                mr: open ? 3 : 'auto',
                                                justifyContent: 'center',
                                            }}
                                        >
                                            <div style={{ color: isSelected(page.path) ? "#aaaaff" : '', }}>{page.icon}</div>
                                        </ListItemIcon>
                                        <ListItemText primary={page.name} sx={{ opacity: open ? 1 : 0, color: isSelected(page.path) ? "#ffffff" : '', }} />
                                    </ListItemButton>
                                </ListItem>
                            ))}
                        </List>
                        <Divider
                            sx={{
                                borderColor: '#2F2F2F', // Cambiar el color del borde
                                borderWidth: '1px 0px 1px 0px', // Cambiar el grosor del borde
                                marginY: 0, // Espaciado vertical
                                marginX: 2, // Espaciado horizontal
                                height: "1px",

                            }}
                        />
                        <List>
                            <h6 style={{ color: "white", textAlign: "center", display: open ? 'block' : 'none' }}>Secundario</h6>
                            {secondaryPages.map((page) => (
                                <ListItem key={page.name} disablePadding sx={{ display: 'block' }}>
                                    <ListItemButton
                                        component={Link}
                                        to={page.path}
                                        sx={{
                                            minHeight: 48,
                                            justifyContent: open ? 'initial' : 'center',
                                            px: 2.5,
                                            //backgroundColor: isSelected(page.path) ? theme.palette.success.main : 'transparent',
                                            backgroundColor: isSelected(page.path) ? "#78787850" : 'transparent',
                                            '&:hover': {
                                                //backgroundColor: theme.palette.success.light,
                                                backgroundColor: "#787878",
                                            },
                                        }}
                                    >
                                        <ListItemIcon
                                            sx={{
                                                minWidth: 0,
                                                mr: open ? 3 : 'auto',
                                                justifyContent: 'center',
                                            }}
                                        >
                                            <div style={{ color: isSelected(page.path) ? "#aaaaff" : '', }}>{page.icon}</div>
                                        </ListItemIcon>
                                        <ListItemText primary={page.name} sx={{ opacity: open ? 1 : 0, color: isSelected(page.path) ? "#ffffff" : '', }} />
                                    </ListItemButton>
                                </ListItem>
                            ))}
                        </List>
                    </div>
                )}
            </Drawer>
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    p: 3,
                    transition: theme.transitions.create('margin', {
                        easing: theme.transitions.easing.sharp,
                        duration: theme.transitions.duration.enteringScreen,
                    }),
                    padding: 0,
                    marginTop: isPortrait ? '10px' : '0',
                    //marginLeft: open ? `${drawerWidth}px` : `calc(${theme.spacing(7)} + 1px)`,
                }}
            >
                <DrawerHeader />
                <Box /* Fondo de todas las paginas */
                    sx={{
                        flex: 1, // Hace que este contenedor crezca para ocupar el espacio restante
                        minHeight: 'calc(100vh - 65px - 72px)', // Ajustar: 65px del AppBar + 72px del Footer
                        backgroundColor: "#121212"
                    }}
                >
                    {children}
                    <div style={{ height: "80px" }} />
                </Box>
            </Box>
        </Box>
    );
}
